import { createAction, isRejected } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { billing, UpgradeSource } from 'common/billing'
import { getPrices } from 'common/billing/actions/billingActions'
import { alert } from 'common/core'
import { Ability } from 'common/core/constants/Ability'
import { getUserId } from 'common/core/selectors/appSelectors'
import * as settingsActionTypes from 'common/settings/SettingsReduxActionTypes'
import { UserRoles } from 'common/userRoles'
import { TEAM_MEMBER_LIMIT } from 'common/userRoles/constants/UserRoles'
import { getCurrentInboxUser } from 'common/userRoles/selectors/userRolesSelectors'
import { User, USER_THREADS_UNLIMITED } from 'common/userRoles/userRolesInterfaces'
import { UsersApi } from 'shared/api/requests/users'
import { createAsyncAction } from 'shared/lib/redux'
import { analyticsService } from 'utils/services/analytics'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

const NAMESPACE = 'userRoles'

export const updateRole = createAsyncAction<void, { role: UserRoles; userId: number }>(
  `${NAMESPACE}/updateRole`,
  async ({ role, userId }) => {
    await UsersApi.update({ body: { role_name: role, user_id: userId } })
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const fetchUsers = createAsyncAction(
  `${NAMESPACE}/fetchUsers`,
  async () => {
    const { data } = await UsersApi.fetch()
    return data.users
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const removeAdmin = createAsyncAction(
  `${NAMESPACE}/removeAdmin`,
  async (userId: number, { dispatch }) => {
    await UsersApi.remove({ body: { user_id: userId } })
    await dispatch(fetchUsers())
    dispatch(fetchCanCreateInviteLink())
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: null,
    },
  },
)

export const updateBillingAccess = createAsyncAction(
  `${NAMESPACE}/updateBillingAccess`,
  async ({ access, userId }: { access: boolean; userId: number }) => {
    await UsersApi.setBillingAccess({ body: { user_id: userId, billing_access: access } })
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const setAddPaidUserModalVisibility = createAction<boolean>(
  `${NAMESPACE}/setAddPaidUserModalVisibility`,
)

export const setDeletePaidUserModalVisibility = createAction<boolean>(
  `${NAMESPACE}/setDeletePaidUserModalVisibility`,
)

export const setBillingAccess = createAction<{ userId: number; access: boolean }>(
  `${NAMESPACE}/setBillingAccess`,
)

export const setUserRole = createAction<{ userId: number; role: UserRoles }>(
  `${NAMESPACE}/setUserRole`,
)

export const addInboxUser = createAsyncAction(
  `${NAMESPACE}/addInboxUser`,
  async (_, { dispatch, getState }) => {
    const { userId } = getCurrentInboxUser(getState())
    if (!userId) {
      alert('User id is not defined', 'danger')
      return
    }

    const result = await dispatch(setInboxUser({ isInboxUser: true, userId }))

    if (isRejected(result)) {
      return
    }

    dispatch(setAddPaidUserModalVisibility(false))
  },
)

export const deleteInboxUser = createAsyncAction(
  `${NAMESPACE}/deleteInboxUser`,
  async (_, { dispatch, getState }) => {
    const { userId } = getCurrentInboxUser(getState())
    if (!userId) {
      alert('User id is not defined', 'danger')
      return
    }

    await dispatch(setInboxUser({ isInboxUser: false, userId })).unwrap()

    dispatch(setDeletePaidUserModalVisibility(false))
    alert(
      l.translate('You deleted Live Chat seat. Your subscription will show the price change.'),
      'success',
    )
  },
)

export const setInboxUser = createAsyncAction(
  `${NAMESPACE}/setInboxUser`,
  async ({ isInboxUser, userId }: { isInboxUser: boolean; userId: number }) => {
    await UsersApi.setInboxUser({
      body: { user_id: userId, is_inbox_user: isInboxUser },
    })
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
    onBusinessError: (error) => {
      if (billing.isProError(error)) {
        billing.handleProError(error, {
          showPaywall: error.error_data.go_pro,
          source: UpgradeSource.SETTINGS_TEAM,
          abilities: [Ability.AUTO_ASSIGN],
        })
        error.handle()
        return
      }

      if (UsersApi.setInboxUser.isBillingAccessError(error)) {
        alert(error.message, 'danger')
        error.handle()
      }
    },
  },
)

export const setConversationsLimits = createAsyncAction(
  `${NAMESPACE}/setConversationsLimits`,
  async ({
    userIds,
    limit,
  }: {
    limit: number | typeof USER_THREADS_UNLIMITED
    userIds: number[]
  }) => {
    await UsersApi.setConversationsLimit({
      body: { limit, user_ids: userIds },
    })

    alert(l.translate('Conversations assignment limit has been changed'), 'success')
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const setAutoAssignmentForUsers = createAsyncAction(
  `${NAMESPACE}/setAutoAssignmentForUsers`,
  async ({ userIds, isEnabled }: { userIds: number[]; isEnabled: boolean }) => {
    await UsersApi.setAutoAssignmentEnabled({
      body: { is_enabled: isEnabled, user_ids: userIds },
    })
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

interface CanSetInboxUserResponse {
  can_set_inbox_user: boolean
}

export const fetchCanSetInboxUser = createAsyncAction(
  `${NAMESPACE}/fetchCanSetInboxUser`,
  async (
    {
      isInboxUser,
      userId,
    }: { isInboxUser: boolean; userId: User['user_id']; userName?: User['name'] },
    { dispatch },
  ) => {
    const { data } = await UsersApi.canSetInboxUser({
      body: { user_id: userId, is_inbox_user: isInboxUser },
    })

    if (data) {
      const { can_set_inbox_user }: CanSetInboxUserResponse = data
      if (can_set_inbox_user) {
        const result = await dispatch(setInboxUser({ isInboxUser, userId }))

        if (isRejected(result)) {
          return
        }

        const alertMessage = isInboxUser
          ? l.translate('You’ve added new Live Chat seat!')
          : l.translate('You’ve successfully deleted Live Chat seat.')
        alert(alertMessage, 'success')
      } else {
        await dispatch(getPrices()).unwrap()

        const showPaidUserModalAction = isInboxUser
          ? setAddPaidUserModalVisibility(true)
          : setDeletePaidUserModalVisibility(true)
        dispatch(showPaidUserModalAction)
      }
    }
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const resetInviteLink = createAction(`${NAMESPACE}/resetInviteLink`)

export const fetchCanCreateInviteLink = createAsyncAction(
  `${NAMESPACE}/fetchCanCreateInviteLink`,
  async () => {
    const data = await UsersApi.canCreateInviteLink()
    return data
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
    onBusinessError: (error) => {
      if (billing.isProError(error)) {
        alert(
          l.translate(
            'Something went wrong on the server, cannot fetch your permissions to create invite link',
          ),
          'danger',
        )
        error.handle()
      }
    },
  },
)

export const generateInviteLink = createAsyncAction(
  `${NAMESPACE}/generateInviteLink`,
  async (role: UserRoles) => {
    const { data } = await UsersApi.generateInviteLink({ body: { role_name: role } })
    return data.link
  },
  {
    onBusinessError: (error) => {
      if (billing.isProError(error)) {
        const message = l.translate(
          `You've reached your limit of ({TEAM_MEMBER_LIMIT}) team member on the Free plan. Add unlimited team members to manage your account with Manychat Pro.`,
          { TEAM_MEMBER_LIMIT },
        )
        analyticsService.sendEvent('SETTINGS.INVITE_NEW_USER.GENERATE_LINK.ERROR')
        billing.handleProError(error, {
          message,
          products: error.error_data.products,
          source: UpgradeSource.MEMBER_LIMIT,
        })
      }

      alert(error.message, 'danger')
      error.handle()
    },
  },
)

anotherTabNotificationsListener.on(
  'account_user_billing_access_update',
  (data, dispatch, getState) => {
    const state = getState() as RootState
    const currentUserId = getUserId(state)
    const userId = data.model.target_user_id
    const billingAccess = data.model.value

    if (currentUserId === userId) {
      dispatch({
        type: settingsActionTypes.SETTINGS_UPDATE_CURRENT_USER_BILLING_ACCESS,
        hasBillingAccess: billingAccess,
      })
    }

    dispatch(setBillingAccess({ userId, access: billingAccess }))
  },
)

anotherTabNotificationsListener.on('account_user_role_update', (data, dispatch, getState) => {
  const state = getState() as RootState
  const currentUserId = getUserId(state)

  if (currentUserId === data.user_id) {
    dispatch({ type: settingsActionTypes.SETTINGS_UPDATE_CURRENT_USER_ROLE, role: data.new_role })
  }

  dispatch(setUserRole({ userId: data.user_id, role: data.new_role }))
})
